import {Box, Typography, Paper, useTheme, Divider} from "@mui/material";
import HotelIcon from "@mui/icons-material/Hotel";
import BathtubIcon from "@mui/icons-material/Bathtub";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import DescriptionIcon from "@mui/icons-material/Description";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HomeIcon from "@mui/icons-material/Home";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import BusinessIcon from "@mui/icons-material/Business";
import VpnKeyIcon from "@mui/icons-material/VpnKey";

interface PropertyDetailsProps {
    details: Record<string, string | number>;
}

// Mapeo de iconos para los detalles
const getIcon = (key: string) => {
    const iconMap: Record<string, any> = {
        Bedrooms: HotelIcon,
        Bathrooms: BathtubIcon,
        "Square Feet": SquareFootIcon,
        "Lot Size": DescriptionIcon,
        "Year Built": CalendarTodayIcon,
        "Property Type": HomeIcon,
        "HOA Fees": AttachMoneyIcon,
        Parking: LocalParkingIcon,
        Cooling: AcUnitIcon,
        Heating: WhatshotIcon,
        Stories: BusinessIcon,
        "MLS #": VpnKeyIcon,
    };

    // Buscar por coincidencia parcial
    for (const [mapKey, Icon] of Object.entries(iconMap)) {
        if (key.toLowerCase().includes(mapKey.toLowerCase()) || mapKey.toLowerCase().includes(key.toLowerCase())) {
            return Icon;
        }
    }

    return DescriptionIcon; // Icono por defecto
};

export default function PropertyDetails({ details }: PropertyDetailsProps) {
    const theme = useTheme();

    return (
        <Paper
            elevation={2}
            sx={{
                p: { xs: 2, md: 3 },
                mt: 4,
                borderRadius: 3,
                backgroundColor: "background.paper",
                width: "100%",
            }}
        >
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                <DescriptionIcon
                    sx={{
                        fontSize: 32,
                        color: theme.palette.primary.main,
                    }}
                />
                <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{
                        color: theme.palette.primary.main,
                    }}
                >
                    Property Details
                </Typography>
            </Box>

            <Divider sx={{ mb: 3, borderColor: '#f8f4f0', borderBottomWidth: '2px' }} />


            {/* Detail Cards Grid */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                    gap: 1,
                }}
            >
                {Object.entries(details).map(([key, value], i) => {
                    const IconComponent = getIcon(key);
                    return (
                        <Paper
                            key={i}
                            elevation={0}
                            sx={{
                                p: 2.5,
                                width: "100%",
                                minHeight: { xs: "auto", sm: 140 },
                                backgroundColor: "#f9f4f1",
                                borderRadius: 2,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                transition: "transform 0.2s ease",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    backgroundColor: "#e8ddd3",
                                },
                            }}
                        >
                            <IconComponent
                                sx={{
                                    fontSize: 36,
                                    color: theme.palette.secondary.main,
                                    mb: 1,
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: "bold",
                                    color: "text.secondary",
                                    textTransform: "uppercase",
                                    fontSize: "0.7rem",
                                    mb: 0.5,
                                }}
                            >
                                {key}
                            </Typography>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                sx={{
                                    color: "text.primary",
                                    fontSize: "1.1rem",
                                }}
                            >
                                {value}
                            </Typography>
                        </Paper>
                    );
                })}
            </Box>
        </Paper>
    );
}
